import React, { useCallback, useEffect, useState } from 'react';
import { Step, Req, reqInput, explain, thaiDate, ListInput } from './ui';
import { downloadXlsx } from './excelExport';

// ===================== 📸 รายงานสต็อกสินค้าสำเร็จ (ส่ง Line ทุกสัปดาห์) =====================
// แทนไฟล์ Daily_Stock_Report.xlsx ของหัวหน้าวางแผนผลิต
// ระบบคำนวณเองทุกช่อง ยกเว้น "รอทดสอบ (ผลิต / PN / ติดตั้ง)" ที่หัวหน้าวางแผนกรอก → กด คัดลอกรูป / ดาวน์โหลดรูป → ส่งเข้ากลุ่ม Line
// สูตรเดียวกับไฟล์ Excel เดิม (ดูรายละเอียดใน sql/W11_fg_report.sql)
const INK = '#1f2a44', MUTED = '#64748b', TEAL = '#1a73e8', RED = '#b42318', GREEN = '#0b7a3e';
const btn = { padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 700, color: INK, whiteSpace: 'nowrap', fontFamily: 'inherit' };
const primary = { ...btn, background: TEAL, borderColor: TEAL, color: '#fff' };
const n = (v) => Number(v) || 0;
const fmt = (v, blankZero) => (blankZero && !n(v) ? '' : n(v).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(new Date());

// สีตามไฟล์ Excel เดิม
const G = {
  prod: { bg: '#FFC000', fg: '#3b2a00', soft: '#fff2cc' },
  wh: { bg: '#70AD47', fg: '#ffffff', soft: '#e2efd9' },
  sum: { bg: '#152AB8', fg: '#ffffff', soft: '#dfe3fb' },
  left: { bg: '#FFFF00', fg: '#3b3b00', soft: '#ffffcc' },
};
// คอลัมน์: key, หัว, กลุ่ม, ความกว้าง (px ในรูป), blankZero = ช่องกรอก/ยอดคลังแสดงว่างเมื่อเป็น 0 (เหมือน Excel)
const COLS = [
  { key: 'wait_prod', label: 'รอผลิต', g: 'prod', w: 70, blankZero: true },
  { key: 'test_prod', label: 'ผลิต', g: 'prod', w: 54, blankZero: true, test: true },
  { key: 'test_pn', label: 'PN', g: 'prod', w: 54, blankZero: true, test: true },
  { key: 'test_install', label: 'ติดตั้ง', g: 'prod', w: 58, blankZero: true, test: true },
  { key: 'in_prod', label: 'ระหว่างผลิต', g: 'prod', w: 92 },
  { key: 'reserved', label: 'จอง\nเพื่อติดตั้ง', g: 'wh', w: 80, blankZero: true },
  { key: 'ready', label: 'พร้อมขาย', g: 'wh', w: 78 },
  { key: 'wh_total', label: 'รวมคลัง', g: 'wh', w: 72, blankZero: true },
  { key: 'wh_plus_prod', label: 'คลัง+ผลิต', g: 'sum', w: 80 },
  { key: 'note_wh', label: 'หมายเหตุ\nคลัง', g: 'sum', w: 80, text: true },
  { key: 'note_prod', label: 'หมายเหตุ\nผลิต', g: 'sum', w: 82, text: true },
  { key: 'after_reserve', label: 'คงเหลือ\nหลังจอง', g: 'left', w: 84 },
];
const GROUPS = [['prod', 'ของฝ่ายผลิต'], ['wh', 'ของในคลัง'], ['sum', 'สรุปข้อมูล']];
const NOTE_STYLE = {
  'ของหมด': { bg: '#E00000', fg: '#ffffff' },
  'ผลิตเพิ่ม': { bg: '#FFC000', fg: '#7a3d00' },
  'รอผลิต': { bg: '#e2efd9', fg: '#2f6d1d' },
};
const cellTone = (c, r) => {
  if (c.text) return NOTE_STYLE[r[c.key]] || null;
  if (c.key === 'ready' && n(r.ready) <= 0) return { bg: null, fg: '#E00000' };
  return null;
};
const reportDate = (rep) => thaiDate(rep?.as_of || today());

// ---------- วาดรูปตาราง (ไว้ส่ง Line) ----------
function drawReport(rep) {
  const rows = rep.rows, S = 2, PART_W = 150, ROW_H = 28, TITLE_H = 34, H1 = 30, H2 = 30, H3 = 26, PAD = 12, FOOT_H = 26;
  const W = PAD * 2 + PART_W + COLS.reduce((t, c) => t + c.w, 0);
  const H = PAD * 2 + TITLE_H + H1 + H2 + H3 + rows.length * ROW_H + FOOT_H;
  const cv = document.createElement('canvas'); cv.width = W * S; cv.height = H * S;
  const x = cv.getContext('2d'); x.scale(S, S);
  const FONT = '"Segoe UI", Tahoma, "Leelawadee UI", sans-serif';
  x.fillStyle = '#ffffff'; x.fillRect(0, 0, W, H);
  const box = (bx, by, bw, bh, text, o = {}) => {
    if (o.bg) { x.fillStyle = o.bg; x.fillRect(bx, by, bw, bh); }
    x.strokeStyle = '#9aa5a8'; x.lineWidth = 1; x.strokeRect(bx + 0.5, by + 0.5, bw, bh);
    if (text === '' || text == null) return;
    x.fillStyle = o.fg || '#111'; x.font = `${o.bold ? '700' : '400'} ${o.size || 13}px ${FONT}`; x.textBaseline = 'middle';
    const lines = String(text).split('\n'), lh = (o.size || 13) + 2;
    lines.forEach((ln, i) => {
      const ty = by + bh / 2 + (i - (lines.length - 1) / 2) * lh;
      if (o.align === 'left') { x.textAlign = 'left'; x.fillText(ln, bx + 6, ty); } else { x.textAlign = 'center'; x.fillText(ln, bx + bw / 2, ty); }
    });
  };
  let y = PAD;
  box(PAD, y, W - PAD * 2, TITLE_H, `อัพเดทสต็อค ณ วันที่ ${reportDate(rep)}`, { bold: true, size: 15 }); y += TITLE_H;
  // หัวตาราง
  const x0 = PAD + PART_W;
  box(PAD, y, PART_W, H1 + H2 + H3, 'Part', { bold: true });
  let cx = x0;
  GROUPS.forEach(([g, label]) => { const w = COLS.filter((c) => c.g === g).reduce((t, c) => t + c.w, 0); box(cx, y, w, H1, label, { bg: G[g].bg, fg: G[g].fg, bold: true }); cx += w; });
  const lastC = COLS[COLS.length - 1];
  box(cx, y, lastC.w, H1 + H2 + H3, lastC.label, { bg: G.left.bg, fg: G.left.fg, bold: true });
  cx = x0; const y2 = y + H1;
  COLS.slice(0, -1).forEach((c) => {
    if (c.test) { if (c.key === 'test_prod') { const tw = COLS.filter((k) => k.test).reduce((t, k) => t + k.w, 0); box(cx, y2, tw, H2, 'รอทดสอบ', { bg: G.prod.soft, bold: true }); }
      box(cx, y2 + H2, c.w, H3, c.label, { bg: G.prod.soft, bold: true, size: 12 }); }
    else box(cx, y2, c.w, H2 + H3, c.label, { bg: G[c.g].soft, bold: true, size: 12 });
    cx += c.w;
  });
  y = y2 + H2 + H3;
  // แถวข้อมูล
  rows.forEach((r) => {
    box(PAD, y, PART_W, ROW_H, r.label, { align: 'left' });
    cx = x0;
    COLS.forEach((c) => { const t = cellTone(c, r) || {}; const v = c.text ? r[c.key] : fmt(r[c.key], c.blankZero);
      box(cx, y, c.w, ROW_H, v, { bg: t.bg || (c.key === 'in_prod' ? '#fff8e1' : c.key === 'after_reserve' ? '#fffff0' : null), fg: t.fg, bold: !!t.bg || c.key === 'ready' || c.key === 'after_reserve' }); cx += c.w; });
    y += ROW_H;
  });
  x.fillStyle = '#64748b'; x.font = `400 11px ${FONT}`; x.textAlign = 'left'; x.textBaseline = 'middle';
  x.fillText(`ยอดคลังจาก Prosoft ณ ${reportDate(rep)} · ส่งจากระบบ HomeXpert คลังสินค้า & ผลิต`, PAD, y + FOOT_H / 2 + 2);
  return cv;
}
const toBlob = (cv) => new Promise((res) => cv.toBlob(res, 'image/png'));

// ---------- หน้า ----------
export default function FgReport({ supabase, codes, refresh }) {
  const [rep, setRep] = useState(null), [err, setErr] = useState(''), [msg, setMsg] = useState(''), [busy, setBusy] = useState(false), [tick, setTick] = useState(0);
  const [edit, setEdit] = useState({}); // id -> {test_prod, test_pn, test_install} ที่ยังไม่บันทึก
  const [manage, setManage] = useState(false), [f, setF] = useState({ label: '', good_code: '' });
  const [preview, setPreview] = useState('');
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => { let off = false;
    supabase.schema('wh').rpc('fg_report').then(({ data, error }) => { if (off) return; if (error) setErr(explain(error)); else { setRep(data); setErr(''); } });
    return () => { off = true; }; }, [supabase, tick, refresh]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  if (err) return <div role="alert" style={{ background: '#fff0e9', border: '1px solid #d48c71', borderRadius: 12, padding: 14, color: '#882b19' }}>อ่านรายงานไม่สำเร็จ: {err}
    {/fg_report|function|schema/i.test(err) && <div style={{ fontSize: 12, marginTop: 4 }}>ต้องรันไฟล์ W11_fg_report.sql ใน Supabase ก่อน</div>}</div>;
  if (!rep) return <p role="status">กำลังโหลดรายงาน…</p>;
  const canEdit = !!rep.can_edit, rows = rep.rows || [];
  const dirty = Object.keys(edit).length > 0;

  const save = async (p, okMsg) => { setBusy(true); const { error } = await supabase.schema('wh').rpc('save_fg_report_item', { p }); setBusy(false);
    if (error) { setMsg('❌ ' + explain(error)); return false; } if (okMsg) setMsg(okMsg); return true; };
  const saveTests = async () => {
    for (const [id, v] of Object.entries(edit)) { if (!(await save({ id, ...v }))) return; }
    setEdit({}); setMsg('✅ บันทึกยอดรอทดสอบแล้ว'); reload();
  };
  const setTest = (r, key, val) => setEdit((e) => ({ ...e, [r.id]: { test_prod: r.test_prod, test_pn: r.test_pn, test_install: r.test_install, ...(e[r.id] || {}), [key]: val } }));
  const testVal = (r, key) => (edit[r.id] && edit[r.id][key] !== undefined ? edit[r.id][key] : (n(r[key]) ? r[key] : ''));

  const makeImage = async () => { if (dirty) { setMsg('❌ กด “บันทึกยอดรอทดสอบ” ก่อน แล้วค่อยทำรูป'); return null; } return toBlob(drawReport(rep)); };
  const copyImg = async () => {
    const b = await makeImage(); if (!b) return;
    try { await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': b })]); setMsg('✅ คัดลอกรูปแล้ว — เปิดกลุ่ม Line (บนคอม) แล้วกด Ctrl+V เพื่อส่ง'); }
    catch { setMsg('❌ เบราว์เซอร์นี้คัดลอกรูปไม่ได้ — ใช้ปุ่ม ดาวน์โหลดรูป แทน'); }
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b); });
  };
  const downloadImg = async () => {
    const b = await makeImage(); if (!b) return;
    const url = URL.createObjectURL(b), a = document.createElement('a');
    a.href = url; a.download = `อัพเดทสต็อค_${(rep.as_of || today()).slice(0, 10)}.png`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    setMsg('✅ ดาวน์โหลดรูปแล้ว — ส่งรูปเข้ากลุ่ม Line ได้เลย');
  };
  const downloadExcel = () => downloadXlsx(`อัพเดทสต็อค_${(rep.as_of || today()).slice(0, 10)}.xlsx`, [{ name: 'อัพเดทสต็อค', columns: [
    { key: 'label', label: 'Part', type: 'text', width: 18 }, { key: 'good_code', label: 'Code WH', type: 'text', width: 22 },
    ...COLS.map((c) => ({ key: c.key, label: (c.test ? 'รอทดสอบ ' : '') + c.label.replace('\n', ''), type: c.text ? 'text' : 'qty' }))], rows }]);

  const move = async (i, dir) => { const a = rows[i], b = rows[i + dir]; if (!a || !b) return;
    if (await save({ id: a.id, sort: b.sort === a.sort ? a.sort + dir : b.sort }) && await save({ id: b.id, sort: a.sort })) reload(); };
  const remove = async (r) => { if (await save({ id: r.id, is_active: false }, `✅ เอา ${r.label} ออกจากรายงานแล้ว`)) reload(); };
  const add = async () => { if (await save({ label: f.label, good_code: f.good_code }, `✅ เพิ่ม ${f.label} แล้ว`)) { setF({ label: '', good_code: '' }); reload(); } };

  const th = (bg, fg, extra) => ({ padding: '6px 8px', background: bg, color: fg, border: '1px solid #9aa5a8', textAlign: 'center', fontSize: 12, fontWeight: 800, whiteSpace: 'pre-line', ...extra });
  const td = { padding: '5px 8px', border: '1px solid #c9d3d5', textAlign: 'center', fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const testCols = COLS.filter((c) => c.test);

  return <div style={{ display: 'grid', gap: 12 }}>
    <Step no={1} title="ตรวจตัวเลข + กรอกยอดรอทดสอบ" hint={canEdit ? 'ระบบคำนวณให้ทุกช่อง ยกเว้น 3 ช่องรอทดสอบ (กรอบส้ม) · ยอดคลังมาจากไฟล์ Prosoft ที่อัปโหลดล่าสุด — อัปโหลดใหม่ก่อนทำรายงาน' : 'ดูอย่างเดียว · ยอดคลังมาจากไฟล์ Prosoft ที่อัปโหลดล่าสุด'}
      right={<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {canEdit && <button style={dirty ? primary : btn} disabled={!dirty || busy} onClick={saveTests}>💾 บันทึกยอดรอทดสอบ</button>}
        {canEdit && <button style={btn} onClick={() => setManage((m) => !m)}>⚙️ จัดการรายการ</button>}
        <button style={btn} onClick={downloadExcel}>⬇️ Excel</button></div>}>
      <table className="hx-plain" style={{ borderCollapse: 'collapse', minWidth: 980, width: '100%' }}>
        <caption style={{ captionSide: 'top', textAlign: 'center', fontWeight: 800, padding: '0 0 8px', color: INK }}>อัพเดทสต็อค ณ วันที่ {reportDate(rep)}</caption>
        <thead>
          <tr><th rowSpan={3} style={th('#f8fafc', INK, { minWidth: 130 })}>Part</th>
            {GROUPS.map(([g, l]) => <th key={g} colSpan={COLS.filter((c) => c.g === g).length} style={th(G[g].bg, G[g].fg)}>{l}</th>)}
            <th rowSpan={3} style={th(G.left.bg, G.left.fg)}>{COLS[COLS.length - 1].label}</th></tr>
          <tr>{COLS.slice(0, -1).map((c) => c.test ? (c.key === 'test_prod' ? <th key="t" colSpan={testCols.length} style={th(G.prod.soft, INK)}>รอทดสอบ</th> : null)
            : <th key={c.key} rowSpan={2} style={th(G[c.g].soft, INK)}>{c.label}</th>)}</tr>
          <tr>{testCols.map((c) => <th key={c.key} style={th(G.prod.soft, INK)}>{c.label}</th>)}</tr>
        </thead>
        <tbody>{rows.map((r) => <tr key={r.id}>
          <td style={{ ...td, textAlign: 'left' }} title={`${r.good_code} · ${r.good_name || ''}`}>{r.label}
            {!r.in_myaccount && <div style={{ fontSize: 11, color: RED, fontWeight: 700 }}>รหัส {r.good_code} ไม่มีใน My Account</div>}</td>
          {COLS.map((c) => { const t = cellTone(c, r) || {};
            if (c.test && canEdit) return <td key={c.key} style={{ ...td, padding: 3, background: '#fff7e8' }}>
              <input type="number" min="0" inputMode="numeric" aria-label={`รอทดสอบ ${c.label} ${r.label}`} value={testVal(r, c.key)} onChange={(e) => setTest(r, c.key, e.target.value)}
                style={{ width: 52, padding: '4px 6px', border: '1.5px solid #f59e0b', borderRadius: 6, textAlign: 'center', fontFamily: 'inherit', fontSize: 13 }} /></td>;
            return <td key={c.key} style={{ ...td, background: t.bg || (c.key === 'in_prod' ? '#fff8e1' : c.key === 'after_reserve' ? '#fffff0' : undefined), color: t.fg, fontWeight: t.bg || c.key === 'ready' || c.key === 'after_reserve' ? 800 : 400 }}>
              {c.text ? r[c.key] : fmt(r[c.key], c.blankZero)}</td>; })}
        </tr>)}</tbody>
      </table>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.6 }}>
        รอผลิต = ใบสั่งผลิตที่ยังไม่จ่ายของ + กำลังผลิต (ยังส่งคืนคลังไม่ครบ) − รอทดสอบ · ระหว่างผลิต = รอผลิต + รอทดสอบ · พร้อมขาย = ตามรายงานสต็อก · รวมคลัง = พร้อมขาย + จอง ·
        คงเหลือหลังจอง = รวมคลัง + ระหว่างผลิต − จอง · หมายเหตุผลิต: พร้อมขายหมด และไม่มีรอผลิต/รอทดสอบติดตั้ง → “ผลิตเพิ่ม”</div>
    </Step>

    {canEdit && manage && <Step no="1.1" tone="#7c3aed" title="จัดการรายการในรายงาน" hint="เพิ่มสินค้า เปลี่ยนลำดับ หรือเอาออก — ชื่อที่แสดงตั้งเองได้ แต่รหัสต้องตรงกับ My Account">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 10 }}>
        <Req no="1" label="ชื่อที่แสดงในรายงาน" flex="1 1 180px"><input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="เช่น B X-9 600" style={reqInput} /></Req>
        <Req no="2" label="รหัสใน My Account" flex="1 1 220px"><ListInput list="fg-codes" value={f.good_code} onChange={(e) => setF({ ...f, good_code: e.target.value })} placeholder="พิมพ์แล้วเลือกจากรายการ" style={reqInput} />
          <datalist id="fg-codes">{(codes || []).map((c) => <option key={c.good_code} value={c.good_code}>{c.good_name}</option>)}</datalist></Req>
        <button style={primary} disabled={busy || !f.label.trim() || !f.good_code.trim()} onClick={add}>＋ เพิ่ม</button>
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>{rows.map((r, i) => <tr key={r.id} style={{ borderBottom: '1px solid #edf2f9' }}>
        <td style={{ padding: 6, width: 30, color: MUTED }}>{i + 1}</td><td style={{ padding: 6, fontWeight: 700 }}>{r.label}</td>
        <td style={{ padding: 6, fontSize: 12, color: MUTED }}>{r.good_code} · {r.good_name || '—'}</td>
        <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button style={{ ...btn, padding: '3px 8px' }} disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label={`เลื่อน ${r.label} ขึ้น`}>↑</button>{' '}
          <button style={{ ...btn, padding: '3px 8px' }} disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`เลื่อน ${r.label} ลง`}>↓</button>{' '}
          <button style={{ ...btn, padding: '3px 8px', color: RED }} disabled={busy} onClick={() => remove(r)}>เอาออก</button></td></tr>)}</tbody></table>
    </Step>}

    <Step no={2} title="ทำรูปแล้วส่งเข้ากลุ่ม Line" hint="บนคอม: กด คัดลอกรูป แล้ว Ctrl+V ในกลุ่ม Line · บนมือถือ: กด ดาวน์โหลดรูป แล้วส่งรูปจากเครื่อง">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={primary} disabled={busy || !rows.length} onClick={copyImg}>📋 คัดลอกรูป</button>
        <button style={btn} disabled={busy || !rows.length} onClick={downloadImg}>📥 ดาวน์โหลดรูป (.png)</button>
        {msg && <span role={msg.startsWith('❌') ? 'alert' : 'status'} style={{ fontSize: 13, fontWeight: 700, color: msg.startsWith('❌') ? RED : GREEN }}>{msg}</span>}
      </div>
      {preview && <div style={{ marginTop: 10 }}><div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>รูปที่คัดลอกไว้:</div>
        <img src={preview} alt="รูปรายงานสต็อกที่จะส่ง Line" style={{ maxWidth: '100%', border: '1px solid #e2e8f2', borderRadius: 8 }} /></div>}
    </Step>
  </div>;
}

export { drawReport, COLS };
